import { useCallback, useEffect, useMemo } from 'react'

import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'

import { Space } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { SpacePicker } from '@tribeplatform/react-components/EntityPicker'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SettingsLayout } from '../../Layout/SettingsLayout.js'

interface FormFields {
  spaces: Space[]
}

export const DefaultSpacesSettings = () => {
  const { $t } = useI18n()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()
  const { completeStep } = useAdminOnboarding()

  const { data: network } = useNetwork({
    fields: {
      defaultSpaces: {
        image: 'all',
      },
    },
  })

  const defaultValues = useMemo(
    () => ({
      spaces: network?.defaultSpaces || [],
    }),
    [network?.defaultSpaces],
  )

  const form = useForm<FormFields>({
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const onSubmit = useCallback(
    async (data: FormFields) => {
      const { spaces } = data
      try {
        await updateNetwork({
          input: {
            defaultSpaceIds: spaces.map(space => space.id),
          },
        })
        completeStep({
          step: 'permission-and-privacy',
        })
        toast({
          title: $t({
            defaultMessage: 'Default {SPACES} updated',
            id: 'Admin.DefaultSpaces.Updated',
          }),
          status: 'success',
        })
        form.reset({ spaces })
      } catch {
        toast({
          title: $t({
            defaultMessage: 'Unable to update default {SPACES}',
            id: 'Admin.DefaultSpaces.FailedToUpdate',
          }),
          status: 'error',
        })
      }
    },
    [$t, completeStep, form, updateNetwork],
  )

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <SettingsLayout
          title={$t({
            defaultMessage: 'Default {SPACES}',
            id: 'Admin.DefaultSpaces',
          })}
          subtitle={$t({
            defaultMessage:
              'Select which {SPACES} you want new Members to be automatically added to.',
            id: 'Admin.DefaultSpaces.Description',
          })}
          disabled={!form.formState.isDirty}
          isLoading={isLoading}
          onCancel={() => form.reset()}
          saveLabel={<T defaultMessage="Update" id="Generics.Update" />}
        >
          <div className="flex flex-col space-y-5">
            <Alert
              status="info"
              title={$t({
                defaultMessage: 'Auto-join {SPACES}',
                id: 'Admin.DefaultSpaces.AutoJoin.Title',
              })}
            >
              <T
                defaultMessage="New members will automatically join the selected {SPACES}. This helps them quickly get started and engaged with your community."
                id="Admin.DefaultSpaces.AutoJoin.Description"
              />
            </Alert>
            <Picker />
          </div>
        </SettingsLayout>
      </form>
    </FormProvider>
  )
}

/**
 * Moved it out of the form because, for some reason, it was causing excessive type
 * inference issue and causing typecheck to hang.
 */
const Picker = () => {
  const { control } = useFormContext()
  return (
    <Controller
      name="spaces"
      control={control}
      render={({ field }) => {
        return (
          <SpacePicker value={field.value} onChange={field.onChange} multiple />
        )
      }}
    />
  )
}
